/**
 * Chart Validators — Bar, line, and pie chart selection question types.
 *
 * Extracted from validatorRegistry.ts during R1-S1 god-file split.
 * Covers: bar_chart, line_chart, pie_chart.
 */

import type { ValidatorFn } from './shared';

// ── Shared Chart Selection ───────────────────────────────────

/**
 * Shared chart selection validator for bar/line/pie charts (DUP-3 fix).
 * Validates user-selected index against correctIndex, with label display.
 */
const validateChartSelection: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer);
  const config = question.data || {};
  const correctIndex = config.correctIndex;

  // Normalize data: support both canonical {data: [{label, value}]} and legacy {labels: [], values: []}
  let data: Array<{
    label: string;
    value: number;
  }> = [];
  if (Array.isArray(config.data) && config.data.length > 0) {
    data = config.data;
  } else if (Array.isArray(config.labels) && Array.isArray(config.values)) {
    data = config.labels.map((label: string, i: number) => ({
      label,
      value: config.values[i] ?? 0
    }));
  }
  const isCorrect = selectedIndex === correctIndex;
  const selectedLabel = data[selectedIndex]?.label || '?';
  const correctLabel = data[correctIndex]?.label || '?';
  return {
    isCorrect,
    distractorIndex: selectedIndex,
    displayAnswer: isCorrect ? correctLabel : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel
  };
};

// ── Exports ──────────────────────────────────────────────────

export const validateBarChart: ValidatorFn = validateChartSelection;
export const validateLineChart: ValidatorFn = validateChartSelection;
export const validatePieChart: ValidatorFn = (userAnswer, question) => {
  const selectedIndex = Number(userAnswer);
  const qData = question.data || {};
  const correctIndex = qData.correctIndex;
  const labels = qData.labels || [];
  const isCorrect = selectedIndex === correctIndex;
  const selectedLabel = labels[selectedIndex] || '?';
  const correctLabel = labels[correctIndex] || '?';
  return {
    isCorrect,
    distractorIndex: selectedIndex,
    displayAnswer: isCorrect ? correctLabel : `${selectedLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel
  };
};